import React, { useContext, useState, useEffect } from 'react';
import {
  Box, AppBar, Toolbar, Typography, Button, Paper, Chip,
  IconButton, Tooltip, Avatar, Divider, useMediaQuery,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import PersonIcon from '@mui/icons-material/Person';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import DownloadIcon from '@mui/icons-material/Download';
import LogoutIcon from '@mui/icons-material/Logout';
import RepresentativeData from '../RepresentativeEdit/RepresentativeData.jsx';
import RepresentativeSearch from './Searches/RepresentativeSearch.jsx';
import UserSearch from './Searches/UserSearch.jsx';
import CSVUpload from './CSVUpload.jsx';
import ClaimApproval from './ClaimApproval/ClaimApproval.jsx';
import Dashboard from '../Dashboard/Dashboard.jsx';
import { AuthContext } from '../Auth/AuthContext.jsx';
import { Navigate } from 'react-router-dom';
import Logout from './Logout.jsx';
import EditTabs from './RepresentativeEdit/EditTabs.jsx';
import './css/dashboard.css';
import { useNavigate } from 'react-router-dom';

// ─── VV Design Tokens ────────────────────────────────────────────────────────
const VV = {
  primary: '#3F2B96',
  secondary: '#7F5AF0',
  accent: '#00C896',
  bg: '#F7F8FC',
  border: 'rgba(63,43,150,0.10)',
};

const NAV_TABS = [
  { label: 'Dashboard', icon: <DashboardIcon fontSize="small" /> },
  { label: 'Representatives', icon: <PeopleIcon fontSize="small" /> },
  { label: 'Users', icon: <PersonIcon fontSize="small" /> },
  { label: 'CSV Upload', icon: <UploadFileIcon fontSize="small" /> },
  { label: 'Claim Requests', icon: <HowToRegIcon fontSize="small" /> },
];

function TabPanel({ children, value, index }) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && children}
    </Box>
  );
}

const MainDashboard = () => {
  const [selectedTab, setSelectedTab] = useState(0);
  const [repId, setRepId] = useState('');
  const [userId, setUserId] = useState('');
  const { isAuthenticated, isAdmin, uid, adminId } = useContext(AuthContext);
  const navigate = useNavigate();

  console.log('🏠 MainDashboard mounted');
  console.log('📊 Dashboard state:', { 
    selectedTab, 
    repId, 
    userId, 
    isAuthenticated, 
    isAdmin, 
    uid, 
    adminId 
  });

  useEffect(() => {
    console.log('🔄 Tab changed to:', selectedTab);
    console.log('📍 Tab name:', ['Dashboard', 'Representatives', 'Users', 'CSV Upload', 'Claim Requests'][selectedTab]);
  }, [selectedTab]);

  useEffect(() => {
    if (uid !== '' && !isAdmin) {
      console.log('👤 Non-admin user detected, redirecting to user edit');
      setUserId(uid);
      navigate('/userEdit/' + adminId);
    }
  }, [navigate]);

  return (
    <>
      {isAuthenticated && isAdmin && (
        <Box sx={{ bgcolor: VV.bg, minHeight: '100vh' }}>

          {/* ── Top Navigation Bar ── */}
          <AppBar
            position="fixed"
            elevation={0}
            sx={{
              background: `linear-gradient(135deg, ${VV.primary} 0%, ${VV.secondary} 100%)`,
              borderBottom: 'none',
              backdropFilter: 'blur(20px)',
              zIndex: 1300,
            }}
          >
            <Toolbar sx={{ minHeight: '60px !important', px: { xs: 2, md: 3 }, gap: 1 }}>

              {/* Brand */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: 3 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: '9px',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ color: '#fff', fontWeight: 900, fontSize: '0.9rem' }}>VV</Typography>
                </Box>
                <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 800, letterSpacing: '-0.3px', display: { xs: 'none', md: 'block' } }}>
                  VoteVocal Admin
                </Typography>
              </Box>

              <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mr: 1 }} />

              {/* Nav Tabs */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, overflowX: 'auto' }}>
                {NAV_TABS.map((tab, index) => (
                  <Button
                    key={index}
                    startIcon={tab.icon}
                    onClick={() => setSelectedTab(index)}
                    sx={{
                      color: selectedTab === index ? '#fff' : 'rgba(255,255,255,0.6)',
                      bgcolor: selectedTab === index ? 'rgba(255,255,255,0.15)' : 'transparent',
                      border: selectedTab === index ? '1px solid rgba(255,255,255,0.25)' : '1px solid transparent',
                      borderRadius: '9px',
                      fontWeight: selectedTab === index ? 700 : 500,
                      fontSize: '0.8rem',
                      textTransform: 'none',
                      whiteSpace: 'nowrap',
                      minWidth: 'auto',
                      px: { xs: 1.5, md: 2 },
                      py: 0.75,
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.12)',
                        color: '#fff',
                      },
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{tab.label}</Box>
                  </Button>
                ))}
              </Box>

              {/* Right Actions */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
                <Tooltip title="Move Pending → Representatives">
                  <Button
                    startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
                    variant="outlined"
                    size="small"
                    href={`${process.env.REACT_APP_PUBLIC_SERVER_URI}/adminWeb/moveDataFromPendingToRepresentative`}
                    target="_blank"
                    sx={{
                      color: 'rgba(255,255,255,0.85)',
                      borderColor: 'rgba(255,255,255,0.3)',
                      borderRadius: '9px',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      display: { xs: 'none', lg: 'flex' },
                      '&:hover': { borderColor: '#fff', color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
                    }}
                  >
                    Sync Pending
                  </Button>
                </Tooltip>

                <Logout isAdmin={isAdmin} />
              </Box>
            </Toolbar>
          </AppBar>

          {/* ── Content Area ── */}
          <Box sx={{ pt: '60px', minHeight: '100vh' }}>
            <TabPanel value={selectedTab} index={0}>
              {console.log('📊 Rendering Dashboard tab')}
              <Dashboard />
            </TabPanel>

            <TabPanel value={selectedTab} index={1}>
              {console.log('👥 Rendering Representatives tab, repId:', repId)}
              {repId ? (
                <>
                  {console.log('✏️ Rendering EditTabs for repId:', repId)}
                  <EditTabs repId={repId} setRepId={setRepId} isAdmin={isAdmin} />
                </>
              ) : (
                <>
                  {console.log('🔍 Rendering RepresentativeSearch')}
                  <RepresentativeSearch setRepId={setRepId} />
                </>
              )}
            </TabPanel>

            <TabPanel value={selectedTab} index={2}>
              {console.log('👤 Rendering Users tab')}
              <UserSearch setUserId={setUserId} />
            </TabPanel>

            <TabPanel value={selectedTab} index={3}>
              <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2, md: 4 }, pt: 3, pb: 14 }}>
                <CSVUpload />
              </Box>
            </TabPanel>

            <TabPanel value={selectedTab} index={4}>
              <ClaimApproval />
            </TabPanel>
          </Box>
        </Box>
      )}
      {!isAuthenticated && <Navigate to="/admin" />}
    </>
  );
};

export default MainDashboard;

